import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import gsap from 'gsap';

const OPACITY_ONLY = ['/products'];

type IdleWin = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

/**
 * Wait for the browser to actually be free before revealing.
 * `deep` = true (leaving the homepage): after two paints, also wait for an idle slot so the
 * heavy WebGL disposal + ScrollTrigger un-pin reflow + navbar restyle all finish UNDER the
 * fully-opaque mask. Otherwise that teardown hitch shows through a half-faded mask (the flash).
 */
const afterSettle = (cb: () => void, deep = false) => {
  let r2 = 0;
  let idle = 0;
  const win = window as IdleWin;
  const r1 = requestAnimationFrame(() => {
    r2 = requestAnimationFrame(() => {
      if (deep && typeof win.requestIdleCallback === 'function') {
        idle = win.requestIdleCallback(cb, { timeout: 260 });
      } else if (deep) {
        idle = window.setTimeout(cb, 90);
      } else {
        cb();
      }
    });
  });
  return () => {
    cancelAnimationFrame(r1);
    cancelAnimationFrame(r2);
    if (idle) {
      win.cancelIdleCallback?.(idle);
      clearTimeout(idle);
    }
  };
};

type Tone = 'dark' | 'light';

const basePrefix = () => {
  const b = __BASE_PATH__.split('/').filter(Boolean).join('/');
  return b ? `/${b}` : '';
};

const stripBase = (p: string) => {
  const pre = basePrefix();
  if (pre && p.startsWith(pre)) return p.slice(pre.length) || '/';
  return p || '/';
};

/**
 * Home transitions are two-phase: the veil (in the DESTINATION tone) fades IN over the
 * current page first, the route swaps while fully covered, then the veil fades OUT.
 * Other page-to-page switches keep the light main/footer crossfade.
 */
export default function RouteTransition() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const prev = useRef<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const covered = useRef(false);
  const busy = useRef(false);

  // Phase 1: intercept internal link clicks that enter/leave the homepage.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement | null)?.closest('a');
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const to = stripBase(url.pathname);
      const from = stripBase(window.location.pathname);
      if (to === from || (to !== '/' && from !== '/')) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const overlay = overlayRef.current;
      if (!overlay) return;
      e.preventDefault();
      e.stopPropagation();
      if (busy.current) return;
      busy.current = true;
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      overlay.dataset.tone = to === '/' ? 'dark' : 'light';
      gsap.killTweensOf(overlay);
      gsap.set(overlay, { display: 'block', opacity: 0, zIndex: from === '/' ? 9999 : 40 });
      gsap.to(overlay, {
        opacity: 1,
        duration: mobile ? 0.24 : 0.36,
        ease: 'sine.inOut',
        onComplete: () => {
          covered.current = true;
          navigate(`${to}${url.search}${url.hash}`);
        },
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [navigate]);

  useLayoutEffect(() => {
    const from = prev.current;
    prev.current = pathname;
    if (from === null || from === pathname) return undefined;
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    const overlay = overlayRef.current;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if ((pathname === '/' || from === '/') && overlay && !reduce) {
      // Back/forward (not intercepted): cover instantly in destination tone.
      if (!covered.current) {
        const destTone: Tone = pathname === '/' && window.scrollY < window.innerHeight * 0.6 ? 'dark' : 'light';
        overlay.dataset.tone = destTone;
        gsap.killTweensOf(overlay);
        gsap.set(overlay, { display: 'block', opacity: 1, zIndex: from === '/' ? 9999 : 40 });
      }
      covered.current = false;
      const leavingHome = from === '/';
      let tween: gsap.core.Tween | null = null;
      // Phase 2: hold the opaque cover until the (expensive, when leaving home) teardown has
      // fully flushed, then dissolve — so no intermediate frame ever shows through.
      const cancel = afterSettle(() => {
        tween = gsap.to(overlay, {
          opacity: 0,
          duration: mobile ? 0.4 : 0.65,
          ease: 'sine.inOut',
          onComplete: () => {
            gsap.set(overlay, { display: 'none', zIndex: 40 });
            busy.current = false;
          },
        });
      }, leavingHome);
      return () => {
        cancel();
        tween?.kill();
        gsap.set(overlay, { opacity: 0, display: 'none' });
        busy.current = false;
      };
    }

    if (reduce) return undefined;
    const targets = Array.from(document.querySelectorAll<HTMLElement>('#root main, #root footer'));
    if (!targets.length) return undefined;
    const move = !mobile && !OPACITY_ONLY.includes(pathname);
    gsap.killTweensOf(targets);
    gsap.set(targets, { opacity: 0, y: move ? 10 : 0 });
    let tween: gsap.core.Tween | null = null;
    const cancel = afterSettle(() => {
      tween = gsap.to(targets, {
        opacity: 1,
        y: 0,
        duration: mobile ? 0.22 : 0.28,
        ease: 'power2.out',
        clearProps: 'opacity,transform',
      });
    });
    return () => {
      cancel();
      tween?.kill();
      gsap.set(targets, { clearProps: 'opacity,transform' });
    };
  }, [pathname]);

  return (
    <div
      ref={overlayRef}
      aria-hidden="true"
      className="route-veil fixed inset-0 z-40 pointer-events-none"
      style={{ display: 'none', opacity: 0, willChange: 'opacity' }}
    />
  );
}